import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import { throwApiError } from '@/shared/utils/apiResponse';
import { lerDadosDaRespostaDoCore } from '@/shared/utils/respostaDoCore';
import type {
  AdminTemplateCreatePayload,
  AdminTemplateMutationResponse,
  AdminTemplateUpdatePayload,
  InfobipImportableTemplate,
  InfobipImportResult,
  InfobipResyncResult,
  WhatsappTemplateLinesPage,
  WhatsappTemplateLinesQuery,
  WhatsappTemplateConfiguration,
  WhatsappTemplateConfigurationResponse,
} from '@/features/admin-geral/types/adminTemplates';
import type {
  ConsultaDeTemplatesWhatsapp,
  ConsultaDeUsosDeTemplateWhatsapp,
  DefinicaoDeFinalidadeInput,
  PaginaDeTemplatesWhatsapp,
  PaginaDeUsosDeTemplateWhatsapp,
  ResultadoDaGestaoWhatsapp,
  ResultadoDaExclusaoWhatsapp,
} from '@/features/admin-geral/types/templatesWhatsappGestao';
import { isWhatsappTemplateLinesResponse } from '../../utils/whatsappTemplateLines';
import { ErroDaGestaoDeTemplateWhatsapp } from './erros';

const lerRespostaDaGestaoWhatsapp = <T>(response: Response, mensagemDeErro: string): Promise<T> =>
  lerDadosDaRespostaDoCore<T>(
    response,
    mensagemDeErro,
    (falha) => new ErroDaGestaoDeTemplateWhatsapp(falha),
  );

export const adminTemplateServices = {
  excluirWhatsappDaGestao: async (templateId: string): Promise<ResultadoDaExclusaoWhatsapp> => {
    const response = await apiDelete(`/admin/templates/${templateId}/whatsapp`);
    return lerRespostaDaGestaoWhatsapp(response, 'Não foi possível excluir o template.');
  },

  getWhatsappConfiguration: async (): Promise<WhatsappTemplateConfiguration> => {
    const response = await apiGet('/extrajudicial/templates/configuracao-whatsapp');
    if (!response.ok)
      await throwApiError(response, 'Não foi possível verificar a configuração de envio.');
    const result: WhatsappTemplateConfigurationResponse = await response.json();
    if (typeof result?.data?.podeEscolherOrigem !== 'boolean') {
      throw new Error('Não foi possível verificar a configuração de envio.');
    }
    return { podeEscolherOrigem: result.data.podeEscolherOrigem };
  },
  listWhatsappLines: async (
    params: WhatsappTemplateLinesQuery,
  ): Promise<WhatsappTemplateLinesPage> => {
    const response = await apiGet('/admin/templates/linhas-disponiveis', {
      page: params.page,
      limit: params.limit,
      ...(params.telefoneInternoId ? { telefoneInternoId: params.telefoneInternoId } : {}),
    });
    if (!response.ok)
      await throwApiError(response, 'Não foi possível buscar as linhas de WhatsApp.');
    const result: unknown = await response.json();
    if (!isWhatsappTemplateLinesResponse(result)) {
      throw new Error('Não foi possível verificar a configuração de envio.');
    }
    const { items, total, page, limit, hasMore, selecionada } = result.data;
    return { items, total, page, limit, hasMore, selecionada };
  },
  listarWhatsappDaGestao: async (
    { page, limit, search, finalidade }: ConsultaDeTemplatesWhatsapp,
    signal?: AbortSignal,
  ): Promise<PaginaDeTemplatesWhatsapp> => {
    const response = await apiGet(
      '/admin/templates/whatsapp',
      { page, limit, ...(search ? { search } : {}), ...(finalidade ? { finalidade } : {}) },
      false,
      signal,
    );
    return lerRespostaDaGestaoWhatsapp(response, 'Não foi possível listar os templates WhatsApp.');
  },

  definirFinalidadeWhatsapp: async ({
    templateId,
    finalidadeWhatsapp,
  }: DefinicaoDeFinalidadeInput): Promise<ResultadoDaGestaoWhatsapp> => {
    const response = await apiPut(`/admin/templates/${templateId}/finalidade-whatsapp`, {
      finalidadeWhatsapp,
    });
    return lerRespostaDaGestaoWhatsapp(response, 'Não foi possível definir a finalidade.');
  },

  listarUsosWhatsapp: async (
    { templateId, page, limit, finalidadeWhatsapp }: ConsultaDeUsosDeTemplateWhatsapp,
    signal?: AbortSignal,
  ): Promise<PaginaDeUsosDeTemplateWhatsapp> => {
    const response = await apiGet(
      `/admin/templates/${templateId}/usos-whatsapp`,
      { page, limit, ...(finalidadeWhatsapp ? { finalidadeWhatsapp } : {}) },
      false,
      signal,
    );
    return lerRespostaDaGestaoWhatsapp(
      response,
      'Não foi possível verificar onde o template é usado.',
    );
  },

  /** Só alcança as contas `AUSENTE` ou `ERRO`; o core torna a repetição segura. */
  redistribuirWhatsapp: async (templateId: string): Promise<ResultadoDaGestaoWhatsapp> => {
    const response = await apiPost(`/admin/templates/${templateId}/whatsapp/distribuir`, {});
    return lerRespostaDaGestaoWhatsapp(response, 'Não foi possível reenviar o template às contas.');
  },

  getById: async (id: string, escritorioId?: string) => {
    const response = await apiGet(
      `/admin/templates/${id}`,
      escritorioId ? { escritorioId } : undefined,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar template');
    }
    return response.json();
  },

  create: async (payload: AdminTemplateCreatePayload): Promise<AdminTemplateMutationResponse> => {
    const response = await apiPost('/admin/templates', payload);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar template');
    }
    return response.json();
  },

  update: async (id: string, payload: AdminTemplateUpdatePayload) => {
    const response = await apiPut(`/admin/templates/${id}`, payload);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar template');
    }
    return response.json();
  },

  publish: async (id: string, escritorioId?: string): Promise<AdminTemplateMutationResponse> => {
    const response = await apiPost(
      `/admin/templates/${id}/publish?escritorioId=${escritorioId || ''}`,
      {},
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao publicar template');
    }
    return response.json();
  },

  // Sem `telefoneInternoId`, o backend varre TODAS as linhas ativas e devolve os templates de
  // cada uma — é o que permite importar de todos os números numa tela só.
  listInfobip: async (
    telefoneInternoId?: string,
  ): Promise<{ data: InfobipImportableTemplate[] }> => {
    const response = await apiGet(
      '/admin/templates/infobip',
      telefoneInternoId ? { telefoneInternoId } : undefined,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao listar templates do Infobip');
    }
    return response.json();
  },

  // A importação é sempre de UMA linha: nome de template é único dentro da WABA, não entre WABAs.
  importInfobip: async (
    templateNames: string[],
    telefoneInternoId?: string,
  ): Promise<{ data: InfobipImportResult }> => {
    const response = await apiPost('/admin/templates/infobip/import', {
      templateNames,
      ...(telefoneInternoId ? { telefoneInternoId } : {}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao importar templates do Infobip');
    }
    return response.json();
  },

  // O resync reescreve os botões do template importado: sem a linha, o mesmo nome em duas WABAs
  // deixaria o alvo ambíguo e a escrita cairia no registro errado.
  syncInfobipButtons: async (
    templateNames: string[],
    telefoneInternoId?: string,
  ): Promise<{ data: InfobipResyncResult }> => {
    const response = await apiPost('/admin/templates/infobip/sync-buttons', {
      templateNames,
      ...(telefoneInternoId ? { telefoneInternoId } : {}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar botões dos templates do Infobip');
    }
    return response.json();
  },
};
